import React, { Fragment, useEffect, useRef, useState } from 'react';
import Footer from '../Footer/Footer';
import Loader from '../../loader/Loader';
import PageSEO from '../Common/PageSEO';
import {
    FeatureHero,
    FeatureHeroCta,
    FeatureSection,
    FeatureDownloadCta
} from '../Common/FeatureContent';
import { useLocale } from '../../localization/LocaleContext';
import { useCurrency } from '../../localization/CurrencyContext';
import { ROUTE_PATHS } from '../../localization/constants';
import './TimeTrackingMain.css';

const PILLAR_ICONS = [
    (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="5 3 19 12 5 21 5 3"/>
        </svg>
    ),
    (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
            <circle cx="12" cy="7" r="4"/>
        </svg>
    ),
    (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
        </svg>
    ),
    (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="6" x2="12" y2="12"/>
            <line x1="16" y1="14" x2="12" y2="12"/>
        </svg>
    ),
    (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
            <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
        </svg>
    ),
    (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <polyline points="14 2 14 8 20 8"/>
        </svg>
    )
];

const PILLAR_TONES = ['violet', 'emerald', 'blue', 'amber', 'teal', 'rose'];

const INITIAL_RECORDS = [
    { id: 1, descKey: 'record1Desc', dateKey: 'record1Date', durationSec: 1320, displayTimeKey: 'record1Duration' },
    { id: 2, descKey: 'record2Desc', dateKey: 'record2Date', durationSec: 27, displayTimeKey: 'record2Duration' }
];

function formatTimerDisplay(sec) {
    const hrs = Math.floor(sec / 3600);
    const mins = Math.floor((sec % 3600) / 60);
    const s = sec % 60;
    const pad = (n) => (n < 10 ? '0' + n : n);
    return `${pad(hrs)}:${pad(mins)}:${pad(s)}`;
}

function TimeTrackingMain(){
    const { t, locale } = useLocale();
    const { formatCurrency } = useCurrency();
    const p = (key) => t(`featurePages.timeTracking.${key}`);

    const [loader, setLoader] = useState(false);
    const [timerSeconds, setTimerSeconds] = useState(20022);
    const [timerRunning, setTimerRunning] = useState(false);
    const [description, setDescription] = useState(p('descriptionDefaultValue'));
    const [records, setRecords] = useState(INITIAL_RECORDS);
    const [searchQuery, setSearchQuery] = useState('');
    const [showSuccess, setShowSuccess] = useState(false);
    const intervalRef = useRef(null);

    useEffect(() => {
        const timer = setTimeout(() => {
            setLoader(true);
        }, 1000);

        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        if (timerRunning) {
            intervalRef.current = setInterval(() => {
                setTimerSeconds((prev) => prev + 1);
            }, 1000);
        } else if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, [timerRunning]);

    if (!loader) {
        return <Loader/>;
    }

    const toggleTimer = () => setTimerRunning((prev) => !prev);

    const resetTimer = () => {
        setTimerRunning(false);
        setTimerSeconds(0);
    };

    const saveCurrentTimer = () => {
        const desc = description || p('descriptionDefaultValue');
        const displayTime = timerSeconds < 60
            ? `${timerSeconds} ${p('secondsShort')}`
            : `${Math.floor(timerSeconds / 60)}${p('minutesShort')} ${timerSeconds % 60}${p('secondsShort')}`;

        const newEntry = {
            id: Date.now(),
            desc,
            date: p('newRecordDate'),
            durationSec: timerSeconds,
            displayTime
        };

        setRecords((prev) => [newEntry, ...prev]);
        resetTimer();
    };

    const convertToInvoice = () => {
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 4000);
    };

    const getRecordDesc = (rec) => rec.desc || p(rec.descKey);
    const getRecordDate = (rec) => rec.date || p(rec.dateKey);
    const getRecordDuration = (rec) => rec.displayTime || p(rec.displayTimeKey);

    const filteredRecords = records.filter((rec) =>
        getRecordDesc(rec).toLowerCase().includes(searchQuery.toLowerCase())
    );

    const ringCircumference = 276;
    const ringOffset = ringCircumference - (ringCircumference * (timerSeconds % 60)) / 60;

    const pillars = [1, 2, 3, 4, 5, 6].map((num) => ({
        icon: PILLAR_ICONS[num - 1],
        tone: PILLAR_TONES[num - 1],
        title: p(`pillar${num}Title`),
        description: p(`pillar${num}Description`)
    }));

    return(
        <Fragment>
            <PageSEO title={p('metaTitle')} description={p('metaDescription')} path={ROUTE_PATHS.timeTracking} locale={locale} />

            <FeatureHero
                eyebrow={p('eyebrow')}
                title={p('heroTitle')}
                highlight={p('heroHighlight')}
                subtitle={p('heroSubtitle')}
                cta={
                    <FeatureHeroCta
                        primaryLabel={t('shared.featureStartFree')}
                        primaryTarget="feature-download"
                        secondaryLabel={t('shared.featureSeeHowItWorks')}
                        secondaryTarget="feature-content"
                    />
                }
            />

            {/* Interactive Timer & Time Logs Suite */}
            <div id="tt-live-timer">
                <FeatureSection id="feature-content" title={p('interactiveSectionTitle')} description={p('interactiveSectionDescription')}>
                <div className="tt-suite-grid">

                    {/* Left: Circular Timer Widget */}
                    <div className="tt-timer-card">
                        <div className="tt-timer-badge">{p('timerBadge')}</div>

                        <div className="tt-timer-circle-wrapper">
                            <svg className="tt-timer-svg" viewBox="0 0 100 100">
                                <circle cx="50" cy="50" r="44" stroke="rgba(255,255,255,0.08)" strokeWidth="3" fill="none"/>
                                <circle cx="50" cy="50" r="38" stroke="rgba(139, 92, 246, 0.2)" strokeWidth="2" fill="none"/>
                                <circle
                                    cx="50" cy="50" r="44"
                                    stroke="#10B981" strokeWidth="4"
                                    strokeDasharray={ringCircumference}
                                    strokeDashoffset={ringOffset}
                                    strokeLinecap="round" fill="none"
                                    className="tt-timer-ring"
                                />
                            </svg>
                            <div className="tt-timer-display-wrap">
                                <span className="tt-timer-display">{formatTimerDisplay(timerSeconds)}</span>
                                <span className={`tt-timer-status${timerRunning ? ' running' : ''}`}>
                                    {timerRunning ? p('timerStatusRunning') : (timerSeconds === 0 ? p('timerStatusReady') : p('timerStatusPaused'))}
                                </span>
                            </div>
                        </div>

                        <div className="tt-timer-action-row">
                            <button
                                type="button"
                                onClick={toggleTimer}
                                className={`tt-main-btn${timerRunning ? ' running' : ''}`}
                                aria-label={timerRunning ? p('timerStatusRunning') : p('timerStatusReady')}
                            >
                                {timerRunning ? (
                                    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
                                ) : (
                                    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><polygon points="6 3 20 12 6 21 6 3"/></svg>
                                )}
                            </button>
                        </div>

                        <div className="tt-desc-field">
                            <label className="tt-desc-label">{p('descriptionLabel')}</label>
                            <input
                                type="text"
                                className="tt-desc-input"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                            />
                        </div>

                        <div className="tt-timer-buttons-row">
                            <button type="button" className="tt-save-btn" onClick={saveCurrentTimer}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="12" y1="5" x2="12" y2="19"/>
                                    <line x1="5" y1="12" x2="19" y2="12"/>
                                </svg>
                                <span>{p('saveEntryButton')}</span>
                            </button>
                            <button type="button" className="tt-reset-btn" onClick={resetTimer} aria-label={p('resetTimerLabel')}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="1 4 1 10 7 10"/>
                                    <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/>
                                </svg>
                            </button>
                        </div>
                    </div>

                    {/* Right: Time Records List */}
                    <div id="tt-time-logs" className="tt-logs-card">
                        <div>
                            <div className="tt-logs-header">
                                <div className="tt-logs-header-left">
                                    <span className="tt-logs-close-pill">{p('logsCloseLabel')}</span>
                                    <h3 className="tt-logs-title">{p('logsTitle')}</h3>
                                </div>
                                <button type="button" className="tt-add-btn" onClick={saveCurrentTimer}>
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                        <line x1="12" y1="5" x2="12" y2="19"/>
                                        <line x1="5" y1="12" x2="19" y2="12"/>
                                    </svg>
                                    <span>{p('addButton')}</span>
                                </button>
                            </div>

                            <div className="tt-search-wrap">
                                <svg className="tt-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="11" cy="11" r="8"/>
                                    <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                                </svg>
                                <input
                                    type="text"
                                    className="tt-search-input"
                                    placeholder={p('searchPlaceholder')}
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                            </div>

                            <div className="tt-records-list">
                                {filteredRecords.map((rec) => (
                                    <div className="tt-record-item" key={rec.id}>
                                        <div className="tt-record-left">
                                            <div className="tt-record-icon">
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <circle cx="12" cy="12" r="9"/>
                                                    <polyline points="12 7 12 12 15 14"/>
                                                </svg>
                                            </div>
                                            <div>
                                                <h4 className="tt-record-name">{getRecordDesc(rec)}</h4>
                                                <span className="tt-record-date">{getRecordDate(rec)}</span>
                                            </div>
                                        </div>
                                        <div className="tt-record-right">
                                            <span className="tt-record-duration">{getRecordDuration(rec)}</span>
                                            <span className="tt-record-billable">{p('billableLabel')}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="tt-convert-box">
                            <div className="tt-convert-row">
                                <div>
                                    <span className="tt-convert-label">{formatCurrency(p('totalSelectedPrice'))}</span>
                                    <span className="tt-convert-value">{p('totalSelectedPrice')}</span>
                                </div>
                                <button type="button" className="tt-convert-btn" onClick={convertToInvoice}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                        <polyline points="14 2 14 8 20 8"/>
                                    </svg>
                                    <span>{p('convertButton')}</span>
                                </button>
                            </div>
                            {showSuccess && (
                                <div className="tt-success-alert">
                                    ✓ {p('convertSuccessMessage')}
                                </div>
                            )}
                        </div>
                    </div>

                </div>
                </FeatureSection>
            </div>

            {/* Feature Pillars Grid */}
            <section className="tt-pillars-section">
                <div className="container">
                    <div className="fc-section-header">
                        <h2 className="h-1 h-bold fc-section-title">{p('pillarsSectionTitle')}</h2>
                        <p className="fc-section-description">{p('pillarsSectionDescription')}</p>
                    </div>
                    <div className="tt-pillars-grid">
                        {pillars.map((pillar, idx) => (
                            <div className={`tt-pillar-card tt-tone-${pillar.tone}`} key={idx}>
                                <div className="tt-pillar-icon-box">{pillar.icon}</div>
                                <h3 className="tt-pillar-title">{pillar.title}</h3>
                                <p className="tt-pillar-desc">{pillar.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <FeatureDownloadCta
                title={p('ctaTitle')}
                description={p('ctaDescription')}
                ctaLabel={p('ctaButton')}
                storesLabel={t('shared.featureDownloadApp')}
            />

            <Footer />
        </Fragment>
    )
}

export default TimeTrackingMain;